import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Breadcrumb from "../../../CommonComponents/BreadCrumb/BreadCrumb";
import CustomTable from "../../../CommonComponents/Table/CustomTable";
import CommonModal from "../../../Modals/CommonModal";
import { networkService } from "../../../config/axios";
import { LEGAL_LANGUAGES, LEGAL_TITLES, formatLegalDate, legalLanguage } from "../../../../utils/legalDocuments";
import "../admin.css";
import "../ApiUsage/adminApiUsage.css";
import "./adminLegalDocuments.css";

const EDITOR_ID = "legal-document-editor";
const KINDS = ["privacy", "terms"];
const PAGE_PATHS = { privacy: "/privacy", terms: "/terms" };

const EDITOR_CONTENT_STYLE = `
  body { font-family: "Public Sans", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif; font-size: 15px; line-height: 1.7; color: #23303d; max-width: 760px; margin: 16px auto; padding: 0 16px; }
  h2 { font-size: 20px; font-weight: 700; color: #294561; margin: 28px 0 10px; padding-top: 20px; border-top: 1px solid #e2e7ee; }
  h3 { font-size: 16px; font-weight: 600; margin: 20px 0 8px; }
  h4 { font-size: 15px; font-weight: 600; margin: 16px 0 6px; }
  p { margin: 0 0 12px; }
  ul, ol { padding-left: 22px; margin: 0 0 14px; }
  li { margin: 0 0 6px; }
  a { color: #294561; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #e2e7ee; padding: 6px 8px; }
`;

const today = () => new Date().toISOString().slice(0, 10);

const loadTinymce = () =>
  new Promise((resolve) => {
    if (window.tinymce) return resolve(window.tinymce);
    const script = document.createElement("script");
    script.src = "/tinymce/tinymce.min.js";
    script.addEventListener("load", () => resolve(window.tinymce));
    document.body.appendChild(script);
  });

const AdminLegalDocumentsPage = () => {
  const [kind, setKind] = useState("privacy");
  const [language, setLanguage] = useState("en");
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [dirty, setDirty] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [requiresReacceptance, setRequiresReacceptance] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const editorRef = useRef(null);
  const dirtyRef = useRef(false);
  const loadSeqRef = useRef(0);

  const markDirty = (value) => {
    dirtyRef.current = value;
    setDirty(value);
  };

  const startingHtml = (data) => data?.draft?.html ?? data?.current?.html ?? data?.fallback?.html ?? "";
  const baselineHtml = startingHtml(state);

  const applyState = useCallback((data) => {
    setState(data);
    markDirty(false);
    const editor = editorRef.current;
    if (editor) {
      editor.setContent(startingHtml(data));
      editor.getWin()?.scrollTo(0, 0);
    }
  }, []);

  const request = async (path, method = "GET", body = undefined) => {
    const { data } = await networkService(`admin/legal_documents/${kind}/${path}.json?lang=${language}`, method, body);
    return data;
  };

  const load = useCallback(async (targetKind, targetLanguage) => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError("");
    try {
      const { data } = await networkService(`admin/legal_documents/${targetKind}/data.json?lang=${targetLanguage}`, "GET");
      if (seq === loadSeqRef.current) applyState(data);
    } catch (e) {
      if (seq === loadSeqRef.current) setError("Could not load this document.");
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [applyState]);

  useEffect(() => {
    let cancelled = false;
    loadTinymce().then((tinymce) => {
      if (cancelled) return;
      tinymce.get(EDITOR_ID)?.remove();
      tinymce.init({
        selector: `#${EDITOR_ID}`,
        height: 620,
        menubar: false,
        statusbar: false,
        plugins: ["autolink", "link", "lists", "table"],
        toolbar: "undo redo | blocks | bold italic underline | bullist numlist | link | table | removeformat",
        block_formats: "Paragraph=p; Section heading=h2; Subheading=h3; Minor heading=h4",
        valid_elements:
          "p,br,h2,h3,h4,strong/b,em/i,u,s,ul,ol,li,a[href|target|rel|title],blockquote,hr,table,thead,tbody,tr,th[colspan|rowspan],td[colspan|rowspan]",
        paste_webkit_styles: "none",
        paste_remove_styles_if_webkit: true,
        link_default_target: "_blank",
        link_assume_external_targets: true,
        table_default_styles: {},
        table_default_attributes: {},
        content_style: EDITOR_CONTENT_STYLE,
        setup(editor) {
          editorRef.current = editor;
          editor.on("init", () => setEditorReady(true));
          editor.on("input change undo redo ExecCommand", () => markDirty(true));
        },
      });
    });
    return () => {
      cancelled = true;
      window.tinymce?.get(EDITOR_ID)?.remove();
      editorRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (editorReady) load(kind, language);
  }, [kind, language, editorReady, load]);

  const run = async (action, successMessage) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const data = await action();
      if (data) applyState(data);
      if (successMessage) setNotice(successMessage);
      return data;
    } catch (e) {
      setError(e?.response?.data?.error || "Something went wrong. Please try again.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const editorHtml = () => editorRef.current?.getContent() || "";

  const saveDraft = () => run(() => request("draft", "PUT", { body_html: editorHtml() }), "Draft saved.");

  const switchTo = async (nextKind, nextLanguage) => {
    if (nextKind === kind && nextLanguage === language) return;
    if (dirtyRef.current) await saveDraft();
    setNotice("");
    setKind(nextKind);
    setLanguage(nextLanguage);
  };

  const languageName = legalLanguage(language).name;
  const livePath = language === "en" ? PAGE_PATHS[kind] : `${PAGE_PATHS[kind]}?lang=${language}`;
  const languageStatus = (code) => {
    const entry = state?.languages?.find((item) => item.code === code);
    if (!entry || entry.published) return entry?.draft ? " · draft" : "";
    return entry.draft ? " · draft, not published" : " · not published";
  };

  const preview = async () => {
    const popup = window.open("", "_blank");
    const data = dirtyRef.current || !state?.draft ? await saveDraft() : state;
    const id = data?.draft?.id;
    if (id && popup) popup.location.href = `${PAGE_PATHS[kind]}?preview=${id}`;
    else popup?.close();
  };

  const publish = async () => {
    setConfirmPublish(false);
    await run(async () => {
      await request("draft", "PUT", { body_html: editorHtml() });
      return request("publish", "POST", { effective_date: effectiveDate, requires_reacceptance: requiresReacceptance });
    }, "Published. The new version is live everywhere.");
    setRequiresReacceptance(false);
    setEffectiveDate(today());
  };

  const discardDraft = () => run(() => request("draft", "DELETE"), "Draft discarded.");
  const markReviewed = () => run(() => request("review", "POST"), "Marked as reviewed today.");
  const restore = (id) => run(() => request(`restore/${id}`, "POST"), "Copied into the draft. Review it, then publish.");

  const canPublish = dirty || !!state?.draft;
  const current = state?.current;

  const columns = useMemo(
    () => [
      { Header: "Version", accessor: "version", Cell: ({ value }) => `v${value}` },
      { Header: "Effective", accessor: "effective_date", Cell: ({ value }) => formatLegalDate(value) || "—" },
      {
        Header: "Published",
        accessor: "published_at",
        Cell: ({ row }) => (
          <div className="kz-legal-admin__stack">
            <span>{row.original.published_at ? new Date(row.original.published_at).toLocaleString() : "—"}</span>
            {row.original.published_by && <small>{row.original.published_by}</small>}
          </div>
        ),
      },
      {
        Header: "Re-accept",
        accessor: "requires_reacceptance",
        Cell: ({ value }) => (value ? <span className="kz-legal-admin__badge">Required</span> : "—"),
      },
      {
        Header: "",
        accessor: "id",
        Cell: ({ row }) => (
          <div className="kz-legal-admin__row-actions">
            <a href={`${PAGE_PATHS[kind]}?preview=${row.original.id}`} target="_blank" rel="noopener noreferrer">
              View
            </a>
            <button type="button" className="kz-legal-admin__link" disabled={busy} onClick={() => restore(row.original.id)}>
              Restore to draft
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kind, language, busy]
  );

  const draftStatus = dirty
    ? "Unsaved changes"
    : state?.draft
      ? `Draft saved ${new Date(state.draft.updated_at).toLocaleString()}${state.draft.updated_by ? ` by ${state.draft.updated_by}` : ""}`
      : state?.current
        ? "No draft. You are editing a copy of the live version."
        : `No ${languageName} edition yet. The editor starts from the English text: replace it with the translation, then publish.`;

  return (
    <div className="admin-module">
      <div className="admin-api-usage-page admin-content kz-legal-admin-page">
        <Breadcrumb string="Admin / Legal Documents" />

        <div className="kz-legal-admin">
          <div className="kz-legal-admin__switcher">
          <div className="kz-legal-admin__tabs" role="tablist">
            {KINDS.map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={kind === k}
                className={`kz-legal-admin__tab${kind === k ? " is-active" : ""}`}
                disabled={busy || loading}
                onClick={() => switchTo(k, language)}
              >
                {LEGAL_TITLES[k]}
              </button>
            ))}
          </div>
          <label className="kz-legal-admin__language">
            <span>Language edition</span>
            <select value={language} disabled={busy || loading} onChange={(e) => switchTo(kind, e.target.value)}>
              {LEGAL_LANGUAGES.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.name}
                  {languageStatus(item.code)}
                </option>
              ))}
            </select>
          </label>
          </div>

          {error && <div className="alert alert-danger small">{error}</div>}
          {notice && <div className="alert alert-success small">{notice}</div>}

          <section className="kz-legal-admin__card kz-legal-admin__live">
            <div>
              <p className="kz-legal-admin__label">Live version · {languageName}</p>
              {current ? (
                <p className="kz-legal-admin__live-meta">
                  <strong>v{current.version}</strong>
                  <span>Effective {formatLegalDate(current.effective_date) || "not set"}</span>
                  <span>
                    Last reviewed {formatLegalDate(current.reviewed_on) || "never"}
                    {current.reviewed_by ? ` by ${current.reviewed_by}` : ""}
                  </span>
                </p>
              ) : (
                <p className="kz-legal-admin__live-meta">
                  {loading
                    ? "Loading…"
                    : language === "en"
                      ? "Nothing published yet."
                      : `No ${languageName} edition published yet. Visitors who choose ${languageName} see the English version.`}
                </p>
              )}
            </div>
            <div className="kz-legal-admin__actions">
              <button type="button" className="kz-legal-admin__btn" disabled={busy || !current} onClick={markReviewed}>
                Mark reviewed today
              </button>
              <a className="kz-legal-admin__btn" href={livePath} target="_blank" rel="noopener noreferrer">
                View live page
              </a>
            </div>
          </section>

          <section className="kz-legal-admin__card">
            <div className="kz-legal-admin__editor-head">
              <div>
                <p className="kz-legal-admin__label">Edit {LEGAL_TITLES[kind]} · {languageName}</p>
                <p className={`kz-legal-admin__draft-status${dirty ? " is-dirty" : ""}`}>{draftStatus}</p>
              </div>
              <div className="kz-legal-admin__actions">
                <button type="button" className="kz-legal-admin__btn" disabled={busy || loading} onClick={saveDraft}>
                  Save draft
                </button>
                <button type="button" className="kz-legal-admin__btn" disabled={busy || loading} onClick={preview}>
                  Preview
                </button>
                {state?.draft && (
                  <button type="button" className="kz-legal-admin__btn kz-legal-admin__btn--danger" disabled={busy} onClick={discardDraft}>
                    Discard draft
                  </button>
                )}
              </div>
            </div>
            <p className="kz-legal-admin__hint">
              Use <strong>Section heading</strong> for each main section; they build the “On this page” menu. Pasted text keeps headings, lists, bold and links, but not fonts or colours.
            </p>
            <div className="kz-legal-admin__editor">
              <textarea id={EDITOR_ID} defaultValue={baselineHtml} />
            </div>

            <div className="kz-legal-admin__publish">
              <label className="kz-legal-admin__field">
                <span>Effective date</span>
                <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
              </label>
              <label className="kz-legal-admin__check">
                <input
                  type="checkbox"
                  checked={requiresReacceptance}
                  onChange={(e) => setRequiresReacceptance(e.target.checked)}
                />
                <span>Material change: ask users to accept again</span>
              </label>
              <button
                type="button"
                className="kz-legal-admin__btn kz-legal-admin__btn--primary"
                disabled={busy || loading || !canPublish}
                onClick={() => setConfirmPublish(true)}
              >
                Publish
              </button>
            </div>
          </section>

          <section className="kz-legal-admin__card">
            <p className="kz-legal-admin__label">Version history</p>
            <div className="top-parent admin-api-usage-table-wrapper">
              {state?.history?.length ? (
                <CustomTable data={state.history} columnData={columns} />
              ) : (
                <p className="admin-api-usage-loading">{loading ? "Loading…" : "No published versions yet."}</p>
              )}
            </div>
          </section>
        </div>
      </div>

      <CommonModal
        modalStatus={confirmPublish}
        changeActiveStatus={setConfirmPublish}
        centered
        title={`Publish ${LEGAL_TITLES[kind]} (${languageName})?`}
        footer={
          <div className="d-flex justify-content-end gap-2 w-100">
            <button type="button" className="kz-legal-admin__btn" onClick={() => setConfirmPublish(false)}>
              Cancel
            </button>
            <button type="button" className="kz-legal-admin__btn kz-legal-admin__btn--primary" onClick={publish}>
              Publish
            </button>
          </div>
        }
      >
        <p className="kz-legal-admin__confirm">
          This replaces the live {languageName} {LEGAL_TITLES[kind]} on {livePath} and in the app, effective{" "}
          {formatLegalDate(effectiveDate) || "today"}.
        </p>
        {requiresReacceptance ? (
          <p className="kz-legal-admin__confirm">
            Every user who accepted an earlier version will be asked to accept it again the next time they open the app.
          </p>
        ) : (
          <p className="kz-legal-admin__confirm">Users will not be asked to accept it again.</p>
        )}
      </CommonModal>
    </div>
  );
};

export default AdminLegalDocumentsPage;
